// Sektör paketleri DR1/DR2/DR3/DR8/DR17 — Set kur (ForkSetModal) ve Sete ekle (AddIndicatorPicker)
// ortak iki sekmeli kaynak seçici: **Paket (n) / Katalog (+a −b)**. Seçim matematiği saf
// `lib/kpi-package-selection.ts`'te; bu bileşen yalnız görünüm + klavye (`role=tablist/tab/tabpanel`,
// ←/→ + Home/End, seçili sekme `tabIndex=0`). Katalog paneli çağırandan gelir (fork: üçlü kutulu ağaç,
// Sete ekle: sette olanı pasif düz liste). Kullanımdan kalkmış kalemli işaretli paketin altında
// DR8 amber satırı: "Seçimden çıkar" (birincil) / "Paketi düzenle ↗" (yeni sekme) / "Yeniden denetle".

import { useEffect, useRef, type KeyboardEvent, type ReactNode } from "react";
import type { KpiPackage } from "@mahir/shared";
import { KPI_PACKAGES_COPY } from "../../lib/kpi-packages-copy";
import type { PackageSourceTab } from "../../lib/kpi-package-selection";
import { LoadingState } from "../common/StateSurface";
import {
  amberBox,
  decisionText,
  footerBtn,
  helpText,
  hitArea44,
  hitArea44Visual,
  warningPill,
} from "./kpi-form-styles";

const C = KPI_PACKAGES_COPY;

export interface PackageRowInfo {
  /** sette olmayan (seçilebilecek) kalem sayısı — Sete ekle "N eksiği seç" */
  missing: number;
  inSet: number;
  total: number;
}

export interface PackageSourceTabsProps {
  idPrefix: string;
  mode: "fork" | "picker";
  tab: PackageSourceTab;
  onTabChange: (tab: PackageSourceTab) => void;
  /** etkin paketler (önerilen üstte); null = yükleniyor */
  packages: readonly KpiPackage[] | null;
  loadFailed: boolean;
  suggested: ReadonlySet<string>;
  /** müşteri sektörü var ama eşleşen paket yok → "Öneri yok, tüm paketler" */
  showNoSuggestion?: boolean;
  checked: readonly string[];
  rowInfo?: (packageId: string) => PackageRowInfo;
  onToggle: (packageId: string, on: boolean) => void;
  onRecheck: () => void;
  disabled?: boolean;
  /** null → etkin paket yok, Katalog sekmesi sayısız */
  catalogCounts: { added: number; removed: number } | null;
  /** DR8 — sunucu reddinden sonra odaklanacak paket satırı (`seq` her istekte artar) */
  focusRequest?: { packageId: string; seq: number } | null;
  /**
   * Açılış kararı verildi mi; `false` iken sekmeler/ağaç yerine iskelet (DR6 yükleme) — erken
   * tıklama sessizce kaybolmaz. Verilmezse hazır sayılır.
   */
  ready?: boolean;
  catalogPanel: ReactNode;
}

const TABS: PackageSourceTab[] = ["package", "catalog"];

export const packageTabId = (p: string, t: PackageSourceTab): string => `${p}-tab-${t}`;
export const packagePanelId = (p: string, t: PackageSourceTab): string => `${p}-panel-${t}`;
export const packageRowId = (p: string, packageId: string): string => `${p}-pkg-${packageId}`;

export function PackageSourceTabs(props: PackageSourceTabsProps): JSX.Element {
  const { idPrefix, tab, onTabChange, focusRequest } = props;
  const tabRefs = useRef<Partial<Record<PackageSourceTab, HTMLButtonElement | null>>>({});

  useEffect(() => {
    if (!focusRequest || tab !== "package") return;
    document.getElementById(packageRowId(idPrefix, focusRequest.packageId))?.focus();
  }, [focusRequest?.seq, tab, idPrefix]);

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>): void {
    const i = TABS.indexOf(tab);
    let next: PackageSourceTab | null = null;
    if (e.key === "ArrowRight") next = TABS[(i + 1) % TABS.length]!;
    else if (e.key === "ArrowLeft") next = TABS[(i - 1 + TABS.length) % TABS.length]!;
    else if (e.key === "Home") next = TABS[0]!;
    else if (e.key === "End") next = TABS[TABS.length - 1]!;
    if (!next) return;
    e.preventDefault();
    onTabChange(next);
    tabRefs.current[next]?.focus();
  }

  const label = (t: PackageSourceTab): string =>
    t === "package"
      ? C.tabs.package(props.checked.length)
      : props.catalogCounts
        ? C.tabs.catalog(props.catalogCounts.added, props.catalogCounts.removed)
        : C.tabs.catalogPlain;

  if (props.ready === false) {
    return (
      <div data-testid="package-source-loading">
        <LoadingState label={C.empty.loadingPackages} />
      </div>
    );
  }

  // Liste hatası / etkin paket yok notu iki sekmede de görünür (panellerin üstünde)
  const notice = props.loadFailed
    ? `${C.page.loadFailed} — ${C.empty.noActivePackage}`
    : props.packages !== null && props.packages.length === 0
      ? C.empty.noActivePackage
      : null;

  return (
    <div className="grid gap-2" data-testid="package-source-tabs">
      <div
        role="tablist"
        aria-label={C.tabs.label}
        className="flex gap-1"
        style={{ borderBottom: "1px solid var(--color-border)" }}
        onKeyDown={onKeyDown}
      >
        {TABS.map((t) => {
          const on = t === tab;
          return (
            <button
              key={t}
              ref={(el) => {
                tabRefs.current[t] = el;
              }}
              type="button"
              role="tab"
              id={packageTabId(idPrefix, t)}
              aria-selected={on}
              aria-controls={packagePanelId(idPrefix, t)}
              tabIndex={on ? 0 : -1}
              onClick={() => onTabChange(t)}
              style={{
                ...decisionText,
                minHeight: 44,
                padding: "8px 14px",
                border: "none",
                borderBottom: `2px solid ${on ? "var(--color-primary)" : "transparent"}`,
                background: "transparent",
                color: on ? "var(--color-primary-text)" : "var(--color-text-2)",
                fontWeight: on ? 700 : 400,
                cursor: "pointer",
              }}
            >
              {label(t)}
            </button>
          );
        })}
      </div>
      {notice ? (
        <p role="status" style={{ ...helpText, margin: 0 }} data-testid="package-source-notice">
          {notice}
        </p>
      ) : null}
      <div
        role="tabpanel"
        id={packagePanelId(idPrefix, "package")}
        aria-labelledby={packageTabId(idPrefix, "package")}
        hidden={tab !== "package"}
      >
        <PackageList {...props} />
      </div>
      <div
        role="tabpanel"
        id={packagePanelId(idPrefix, "catalog")}
        aria-labelledby={packageTabId(idPrefix, "catalog")}
        hidden={tab !== "catalog"}
      >
        {props.catalogPanel}
      </div>
    </div>
  );
}

function PackageList(props: PackageSourceTabsProps): JSX.Element | null {
  const { packages, idPrefix, mode, checked, disabled } = props;
  // hata / boş liste notu panellerin üstünde (iki sekmede ortak)
  if (props.loadFailed) return null;
  if (packages === null) return <LoadingState label={C.empty.loadingPackages} />;
  if (packages.length === 0) return null;
  const infos = packages.map((p) => props.rowInfo?.(p.id) ?? null);
  const nothingToAdd = mode === "picker" && infos.every((i) => i !== null && i.missing === 0);
  return (
    <div className="grid gap-2">
      {props.showNoSuggestion ? <p style={helpText}>{C.empty.noSuggestion}</p> : null}
      {nothingToAdd ? (
        <p role="status" style={helpText}>
          {C.pickerAction.nothingToAdd}
        </p>
      ) : null}
      <ul
        aria-label={C.page.rail}
        className="rounded-lg"
        style={{
          listStyle: "none",
          margin: 0,
          padding: 0,
          border: "1px solid var(--color-border)",
          maxHeight: 320,
          overflowY: "auto",
        }}
      >
        {packages.map((p, i) => {
          const on = checked.includes(p.id);
          const info = infos[i] ?? null;
          return (
            <li
              key={p.id}
              id={packageRowId(idPrefix, p.id)}
              tabIndex={-1}
              data-testid="package-row"
              style={{ borderBottom: "1px solid var(--color-border)", padding: "4px 12px" }}
            >
              <PackageRow
                pkg={p}
                on={on}
                mode={mode}
                info={info}
                suggested={props.suggested.has(p.id)}
                disabled={disabled === true}
                onToggle={props.onToggle}
              />
              {on && p.deprecatedCount > 0 ? (
                <DeprecatedRow
                  pkg={p}
                  mode={mode}
                  disabled={disabled === true}
                  onRemove={() => props.onToggle(p.id, false)}
                  onRecheck={props.onRecheck}
                />
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function PackageRow(p: {
  pkg: KpiPackage;
  on: boolean;
  mode: "fork" | "picker";
  info: PackageRowInfo | null;
  suggested: boolean;
  disabled: boolean;
  onToggle: (id: string, on: boolean) => void;
}): JSX.Element {
  const { pkg, on, info } = p;
  const pills = (
    <>
      {p.suggested ? (
        <span style={{ ...helpText, color: "var(--color-text-3)", marginLeft: 6 }}>
          {C.pills.suggested}
        </span>
      ) : null}
      {pkg.deprecatedCount > 0 ? (
        <span style={{ ...warningPill, marginLeft: 6 }}>{C.pills.deprecated}</span>
      ) : null}
    </>
  );
  if (p.mode === "fork") {
    return (
      <label
        className="flex items-center gap-3"
        style={{ ...decisionText, minHeight: 44, cursor: p.disabled ? "default" : "pointer" }}
      >
        <input
          type="checkbox"
          checked={on}
          disabled={p.disabled}
          onChange={(e) => p.onToggle(pkg.id, e.target.checked)}
          style={{ width: 18, height: 18 }}
        />
        <span className="min-w-0 flex-1">
          {pkg.name}
          {pills}
        </span>
        <span style={{ ...helpText, whiteSpace: "nowrap" }}>{C.summary.total(pkg.itemCount)}</span>
      </label>
    );
  }
  const missing = info?.missing ?? 0;
  const full = info !== null && missing === 0;
  const text = on
    ? C.pickerAction.unselect
    : full
      ? C.pickerAction.allInSet
      : C.pickerAction.selectMissing(missing);
  return (
    <div className="flex items-center gap-3" style={{ ...decisionText, minHeight: 44 }}>
      <span className="min-w-0 flex-1">
        {pkg.name}
        {pills}
      </span>
      {info ? (
        <span style={{ ...helpText, whiteSpace: "nowrap" }}>
          {C.pickerAction.inSetCount(info.inSet, info.total)}
        </span>
      ) : null}
      <button
        type="button"
        aria-pressed={on}
        disabled={p.disabled || info === null || (full && !on)}
        onClick={() => p.onToggle(pkg.id, !on)}
        style={hitArea44}
      >
        <span style={{ ...hitArea44Visual, fontWeight: on ? 600 : 400 }}>{text}</span>
      </button>
    </div>
  );
}

function DeprecatedRow(p: {
  pkg: KpiPackage;
  mode: "fork" | "picker";
  disabled: boolean;
  onRemove: () => void;
  onRecheck: () => void;
}): JSX.Element {
  const D = C.deprecated;
  return (
    <div
      className="grid gap-2"
      style={{ ...amberBox, margin: "4px 0 8px" }}
      data-testid="package-deprecated"
    >
      <span>
        {p.mode === "fork" ? D.forkRow(p.pkg.deprecatedCount) : D.pickerRow(p.pkg.deprecatedCount)}
      </span>
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" style={footerBtn(true)} disabled={p.disabled} onClick={p.onRemove}>
          {D.removeFromSelection}
        </button>
        <a
          href={`/clients/kpi/paketler?packageId=${encodeURIComponent(p.pkg.id)}`}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={D.editPackageLabel(p.pkg.name)}
          style={{
            ...footerBtn(),
            display: "inline-flex",
            alignItems: "center",
            textDecoration: "none",
          }}
        >
          {D.editPackage}
        </a>
        <button type="button" style={footerBtn()} disabled={p.disabled} onClick={p.onRecheck}>
          {D.recheck}
        </button>
      </div>
    </div>
  );
}
